"use client";

import * as React from "react";
import { cn } from "@/ui/cn";
import { ToggleGroup, ToggleGroupItem } from "@/ui/toggle-group";

export type SegmentedControlOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
};

// Breakpoint at which the control switches from stacked to horizontal.
// "eager" ~200px  — spreads as soon as there's any room
// "auto"  ~320px  — default; horizontal in most panels and dialogs
// "lazy"  ~500px  — stays stacked until quite wide
const WRAP = {
  eager: {
    row: "@[200px]:flex-row",
    flex1: "@[200px]:flex-1",
    rounded: "@max-[200px]:rounded-none!",
    borderL: "@max-[200px]:border-l!",
    firstRound: "@max-[200px]:first:rounded-t-md!",
    lastRound: "@max-[200px]:last:rounded-b-md!",
    borderT: "@max-[200px]:[&:not(:first-child)]:border-t-0",
  },
  auto: {
    row: "@[320px]:flex-row",
    flex1: "@[320px]:flex-1",
    rounded: "@max-[320px]:rounded-none!",
    borderL: "@max-[320px]:border-l!",
    firstRound: "@max-[320px]:first:rounded-t-md!",
    lastRound: "@max-[320px]:last:rounded-b-md!",
    borderT: "@max-[320px]:[&:not(:first-child)]:border-t-0",
  },
  lazy: {
    row: "@[500px]:flex-row",
    flex1: "@[500px]:flex-1",
    rounded: "@max-[500px]:rounded-none!",
    borderL: "@max-[500px]:border-l!",
    firstRound: "@max-[500px]:first:rounded-t-md!",
    lastRound: "@max-[500px]:last:rounded-b-md!",
    borderT: "@max-[500px]:[&:not(:first-child)]:border-t-0",
  },
} as const;

export function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  wrap = "auto",
  className,
  name,
  disabled,
  id,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: {
  options: SegmentedControlOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  wrap?: keyof typeof WRAP;
  className?: string;
  disabled?: boolean;
  /** Mirror the value into a hidden input so it serializes into FormData under `<Form action>`. */
  name?: string;
  /** Applied to the `radiogroup`, so a `FormGroupControl` can name and describe it. */
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const bp = WRAP[wrap];
  return (
    <div className="@container w-full">
      {name && <input type="hidden" name={name} value={value} />}
      <ToggleGroup
        type="single"
        variant="outline"
        value={value}
        onValueChange={(v) => {
          if (v) onValueChange(v as T);
        }}
        id={id}
        disabled={disabled}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        className={cn("w-full flex-col", bp.row, className)}
      >
        {options.map((opt) => (
          <ToggleGroupItem
            key={opt.value}
            value={opt.value}
            disabled={opt.disabled}
            className={cn(
              "gap-1.5 text-xs font-bold tracking-wide",
              // The default on-state (bg-accent) is nearly invisible in dark mode, where
              // accent ≈ the resting surface. An inset primary ring + primary text marks the
              // selected segment the same way the design system's switches mark "on".
              "data-[state=on]:z-10 data-[state=on]:ring-1 data-[state=on]:ring-inset data-[state=on]:ring-primary data-[state=on]:text-primary-text",
              "w-full",
              bp.flex1,
              bp.rounded,
              bp.borderL,
              bp.firstRound,
              bp.lastRound,
              bp.borderT
            )}
          >
            {opt.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
